import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  Visibility,
  TrendingUp,
  Assessment,
  Close
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { useAuth } from '../App';

const TestResults = () => {
  const [results, setResults] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    fetchTestsAndResults();

    // If navigated here with new result, show it
    if (location.state?.newResult && location.state?.showDetails) {
      setSelectedResult(location.state.newResult);
      setShowDetailDialog(true);
    }
  }, [location.state]);

  const fetchTestsAndResults = async (filterTestId = '') => {
    setLoading(true);
    setError('');
    try {
      const [testsRes, resultsRes] = await Promise.all([
        axios.get('/api/tests'),
        axios.get('/api/tests/results', { params: filterTestId ? { testId: filterTestId } : {} })
      ]);
      setTests(testsRes.data.tests || []);
      setResults(resultsRes.data.results || []);
    } catch (e) {
      setError('Failed to load test results');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (e) => {
    const newTestId = e.target.value;
    setSelectedTestId(newTestId);
    await fetchTestsAndResults(newTestId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getGrade = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  const viewDetailedResult = async (resultId) => {
    try {
      const response = await axios.get(`/api/tests/results/${resultId}`);
      setSelectedResult(response.data.result);
      setShowDetailDialog(true);
    } catch (error) {
      setError('Failed to load detailed result');
    }
  };

  const deleteResult = async (resultId) => {
    if (!window.confirm('Delete this test result? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/tests/results/${resultId}`);
      setResults(prev => prev.filter(r => r.id !== resultId));
    } catch (e) {
      setError('Failed to delete test result');
    }
  };

  // Calculate statistics
  const totalTests = results.length;
  const averageScore = totalTests > 0
    ? Math.round(results.reduce((sum, result) => sum + result.score, 0) / totalTests)
    : 0;
  const bestScore = totalTests > 0
    ? Math.max(...results.map(result => result.score))
    : 0;
  const passedTests = results.filter(result => result.score >= 60).length;

  // Prepare chart data
  const chartData = results
    .slice(-10)
    .map((result, index) => ({
      test: `Test ${index + 1}`,
      score: result.score,
      date: new Date(result.submittedAt).toLocaleDateString()
    }));

  // Grade distribution
  const gradeDistribution = results.reduce((acc, result) => {
    const grade = getGrade(result.score);
    acc[grade] = (acc[grade] || 0) + 1;
    return acc;
  }, {});

  const gradeData = Object.entries(gradeDistribution).map(([grade, count]) => ({
    name: grade,
    value: count
  }));

  const COLORS = ['#4caf50', '#8bc34a', '#ffeb3b', '#ff9800', '#f44336', '#9c27b0'];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Test Results
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {user?.role === 'admin' ? 'All student test results' : 'Your test history and performance analytics'}
        </Typography>
        {user?.role === 'admin' && (
          <Box sx={{ mt: 2, maxWidth: 400 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter by Test</Typography>
            <select value={selectedTestId} onChange={handleFilterChange} style={{ width: '100%', padding: '8px' }}>
              <option value="">All Tests</option>
              {tests.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Box>
        )}
      </Box>

      {results.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No test results found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.role === 'admin'
              ? 'Students haven\'t taken any tests yet.'
              : 'You haven\'t taken any tests yet. Take your first test to see results here.'
            }
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Statistics Cards - Only for students */}
          {user?.role === 'student' && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {totalTests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tests Taken
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {averageScore}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Score
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {bestScore}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best Score
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {passedTests}/{totalTests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Passed Tests
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Charts - Only for students */}
          {user?.role === 'student' && chartData.length > 1 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Performance Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="test" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#1976d2"
                        strokeWidth={2}
                        dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Grade Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={gradeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {gradeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Results List */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {user?.role === 'admin' ? 'All Test Results' : 'Test History'}
            </Typography>
            <List>
              {results.map((result, index) => (
                <ListItem
                  key={result.id}
                  divider={index < results.length - 1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="subtitle1">
                          {user?.role === 'admin' ? result.userName : (result.testName || 'Untitled Test')}
                        </Typography>
                        <Chip
                          label={getGrade(result.score)}
                          color={getScoreColor(result.score)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {user?.role === 'admin' ? `${result.testName || 'Untitled Test'} • ` : ''}
                          {formatDate(result.submittedAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Score: {result.correctAnswers}/{result.totalQuestions} questions
                        </Typography>
                      </Box>
                    }
                  />
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={`${result.score}%`}
                      color={getScoreColor(result.score)}
                      variant="filled"
                      sx={{ minWidth: 60 }}
                    />
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => viewDetailedResult(result.id)}
                    >
                      View Details
                    </Button>
                    {user?.role === 'admin' && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deleteResult(result.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </>
      )}

      {/* Detailed Result Dialog */}
      <Dialog
        open={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Test Result Details
            </Typography>
            <Button onClick={() => setShowDetailDialog(false)}>
              <Close />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box>
              {/* Result Summary */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Test</Typography>
                      <Typography variant="body1">{selectedResult.testName || 'Untitled Test'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Date Taken</Typography>
                      <Typography variant="body1">{formatDate(selectedResult.submittedAt)}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Score</Typography>
                      <Typography variant="h4" color={`${getScoreColor(selectedResult.score)}.main`}>
                        {selectedResult.score}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Grade</Typography>
                      <Typography variant="h4" color={`${getScoreColor(selectedResult.score)}.main`}>
                        {getGrade(selectedResult.score)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Correct Answers</Typography>
                      <Typography variant="h4" color="success.main">
                        {selectedResult.correctAnswers}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Total Questions</Typography>
                      <Typography variant="h4">
                        {selectedResult.totalQuestions}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Question-by-Question Results */}
              <Typography variant="h6" gutterBottom>
                Question-by-Question Review
              </Typography>
              {selectedResult.detailedResults?.map((detail, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Box display="flex" alignItems="center" mr={2}>
                        {detail.isCorrect ? (
                          <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                        ) : (
                          <Cancel sx={{ color: 'error.main', mr: 1 }} />
                        )}
                        <Typography variant="subtitle1">
                          Question {index + 1}
                        </Typography>
                      </Box>
                      <Box flexGrow={1} />
                      <Chip
                        size="small"
                        label={detail.isCorrect ? 'Correct' : 'Incorrect'}
                        color={detail.isCorrect ? 'success' : 'error'}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        <strong>Question:</strong> {detail.question}
                      </Typography>

                      <Box my={2}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Your Answer:
                        </Typography>
                        <Typography
                          variant="body1"
                          color={detail.isCorrect ? 'success.main' : 'error.main'}
                        >
                          {detail.userAnswer !== -1 ? `Option ${detail.userAnswer + 1}` : 'No answer selected'}
                        </Typography>
                      </Box>

                      {!detail.isCorrect && (
                        <Box my={2}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Correct Answer:
                          </Typography>
                          <Typography variant="body1" color="success.main">
                            Option {detail.correctAnswer + 1}
                          </Typography>
                        </Box>
                      )}

                      {detail.explanation && (
                        <Box my={2}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Explanation:
                          </Typography>
                          <Typography variant="body2">
                            {detail.explanation}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestResults; 